/**
 * BreadcrumbContext
 *
 * Provides breadcrumb navigation data to the main Navigation component.
 * Pages can set breadcrumbs which will be displayed in the header.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

/**
 * Breadcrumb item representing a single link in the trail
 */
export interface BreadcrumbItem {
  /** Display label */
  label: string;
  /** Navigation path (optional - current page won't have one) */
  path?: string;
}

/**
 * Context value type
 */
interface BreadcrumbContextType {
  /** Array of breadcrumb items */
  breadcrumbs: BreadcrumbItem[];
  /** Set breadcrumbs from a page */
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  /** Clear breadcrumbs */
  clearBreadcrumbs: () => void;
  /** Back link path (first navigable item) */
  backPath: string | null;
}

const BreadcrumbContext = createContext<BreadcrumbContextType | null>(null);

/**
 * BreadcrumbProvider wraps the app to provide breadcrumb state
 */
export const BreadcrumbProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbsState] = useState<BreadcrumbItem[]>([]);

  const setBreadcrumbs = useCallback((items: BreadcrumbItem[]) => {
    setBreadcrumbsState(items);
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbsState([]);
  }, []);

  // Find the back path - the last navigable item before current
  const backPath =
    breadcrumbs.length > 1
      ? (breadcrumbs
          .slice(0, -1)
          .reverse()
          .find((b) => b.path)?.path ?? null)
      : null;

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, setBreadcrumbs, clearBreadcrumbs, backPath }}>
      {children}
    </BreadcrumbContext.Provider>
  );
};

/**
 * Hook to access breadcrumb context
 */
export const useBreadcrumbs = (): BreadcrumbContextType => {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }
  return context;
};

export default BreadcrumbContext;
